// src/pages/document/DocumentNumberingSchemeListPage.tsx
// Auto-generated by corebuilder. Edit freely.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useDocument, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
  Badge,
} from '@react/components';

const PATTERN_KEYS: Record<string, string> = {
  SEQUENTIAL: 'document.numberingField.patternSequential',
  YEAR_SEQUENTIAL: 'document.numberingField.patternYearSequential',
  CATEGORY_SEQUENTIAL: 'document.numberingField.patternCategorySequential',
  YEAR_CATEGORY: 'document.numberingField.patternYearCategory',
};

/**
 * Document numbering scheme list page.
 */
export const DocumentNumberingSchemeListPage = observer(() => {
  const store = useDocument();
  const { t } = useI18n();
  const [items, setItems] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await store.ListNumberingSchemes();
        const schemes = res?.schemes?.schemes;
        setItems(Array.isArray(schemes) ? schemes : []);
      } catch (err: unknown) {
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="space-y-6">
      {/* Page header — the tab already names this page, so the heading is screen-reader only. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="sr-only">{t('document.numberingSchemes')}</h1>
        <nav className="text-sm">
          <Link to="/documents" className="text-muted-foreground hover:text-foreground">
            {t('document.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('document.numberingSchemes')}</span>
        </nav>
        <Link to="/documents/numbering-schemes/new" className="sm:ml-auto">
          <Button>{t('common.create')}</Button>
        </Link>
      </div>

      {error ? (
        <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />
      ) : null}

      <Card>
        <Table hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('document.numberingField.name')}</TableHead>
              <TableHead>{t('document.numberingField.pattern')}</TableHead>
              <TableHead>{t('document.numberingField.prefix')}</TableHead>
              <TableHead>{t('document.numberingField.counterDigits')}</TableHead>
              <TableHead>{t('document.numberingField.active')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableEmpty colSpan={5} message={t('document.noItems')} />
            ) : (
              items.map((item: any) => (
                <TableRow key={item.entity?.id || item.id || Math.random()}>
                  <TableCell>
                    <span className="font-medium text-foreground">{item.name || '—'}</span>
                    {item.description && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{item.description}</div>}
                  </TableCell>
                  <TableCell>
                    {PATTERN_KEYS[String(item.pattern || '').replace(/^NUMBERING_PATTERN_/, '')] ? t(PATTERN_KEYS[String(item.pattern).replace(/^NUMBERING_PATTERN_/, '')]) : '—'}
                  </TableCell>
                  <TableCell>
                    {item.prefix || '—'}
                  </TableCell>
                  <TableCell>
                    {item.counter_digits ?? '—'}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={item.is_active ? 'success' : 'secondary'}
                      size="sm"
                    >
                      {item.is_active ? t('common.yes') : t('common.no')}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default DocumentNumberingSchemeListPage;
